/**
 * CopilotSearchWidget — floating "Ask the docs" bubble.
 *
 * Sits bottom-left (SmartOperatorBubble already claims bottom-right).
 * Opens a modal with a search box that hits:
 *   POST /api/copilot/search  → semantic docs search (instant, free)
 *   POST /api/copilot/ask     → RAG Q&A with Claude + citations
 *
 * Falls back gracefully when the LLM key is depleted (backend returns
 * source: "fallback" and the widget shows the stitched-together snippet).
 */
import { useEffect, useRef, useState } from "react";
import { useLocation } from "react-router-dom";
import { X, Sparkles, Search, ArrowRight, ExternalLink, Loader2 } from "lucide-react";
import { friendlyError } from "@/lib/friendlyError";

const API = process.env.REACT_APP_BACKEND_URL;

export default function CopilotSearchWidget() {
  const location = useLocation();
  // Hide on public / marketing pages where "Mark" (SmartChatBubble) is the
  // primary helper. Keeps the UI uncluttered for first-time visitors.
  const PUBLIC_PREFIXES = ["/login", "/signup", "/forgot", "/reset", "/accept-invite", "/ad/", "/about", "/pricing", "/features", "/tools", "/answers", "/blog", "/vs", "/for-", "/property-management", "/directory", "/press", "/investors"];
  const isPublicRoute = location.pathname === "/" || PUBLIC_PREFIXES.some((p) => location.pathname.startsWith(p));
  const [open, setOpen] = useState(false);
  const [hidden, setHidden] = useState(() => {
    try { return localStorage.getItem("crc_copilot_hidden") === "1"; } catch { return false; }
  });
  const dismiss = (e) => {
    e.stopPropagation();
    e.preventDefault();
    try { localStorage.setItem("crc_copilot_hidden", "1"); } catch {}
    setHidden(true);
  };
  const [mode, setMode] = useState("ask"); // "ask" | "search"
  const [q, setQ] = useState("");
  const [loading, setLoading] = useState(false);
  const [answer, setAnswer] = useState(null); // {answer, citations, source}
  const [hits, setHits] = useState([]);
  const [error, setError] = useState("");
  const inputRef = useRef(null);

  useEffect(() => {
    if (open) setTimeout(() => inputRef.current?.focus(), 50);
  }, [open]);

  // Keyboard shortcut: Cmd/Ctrl+K opens the widget
  useEffect(() => {
    function handler(e) {
      if ((e.metaKey || e.ctrlKey) && e.key.toLowerCase() === "k") {
        e.preventDefault();
        setOpen((v) => !v);
      }
      if (e.key === "Escape" && open) setOpen(false);
    }
    window.addEventListener("keydown", handler);
    return () => window.removeEventListener("keydown", handler);
  }, [open]);

  // Public / marketing routes hand the primary helper role to Mark
  // (SmartChatBubble). Bail out cleanly here so we don't stack bubbles.
  if (isPublicRoute) return null;

  async function submit() {
    if (!q.trim()) return;
    setLoading(true);
    setError("");
    setAnswer(null);
    setHits([]);
    try {
      if (mode === "ask") {
        const r = await fetch(`${API}/api/copilot/ask`, {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({ question: q }),
        });
        if (!r.ok) {
          const j = await r.json().catch(() => ({}));
          if (j?.answer || j?.body) { setAnswer({ answer: j.answer || j.body, sources: j.sources || [], fallback: true }); return; }
          throw new Error(friendlyError({ response: { status: r.status, data: j } }, "Copilot is briefly unavailable."));
        }
        const data = await r.json();
        setAnswer(data);
      } else {
        const r = await fetch(`${API}/api/copilot/search`, {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({ query: q, k: 6 }),
        });
        if (!r.ok) {
          const j = await r.json().catch(() => ({}));
          throw new Error(friendlyError({ response: { status: r.status, data: j } }, "Copilot is briefly unavailable."));
        }
        const data = await r.json();
        setHits(data.hits || []);
      }
    } catch (e) {
      setError(friendlyError(e, "Copilot is briefly unavailable. Try again in a moment."));
    } finally {
      setLoading(false);
    }
  }

  return (
    <>
      {/* Floating trigger */}
      {!hidden && (
      <div className="fixed bottom-24 left-4 lg:bottom-6 lg:left-6 z-30 inline-flex items-center">
      <button
        data-testid="copilot-open-btn"
        onClick={() => setOpen(true)}
        className="flex items-center gap-2 rounded-full bg-white/95 backdrop-blur shadow-lg border border-slate-200 px-4 py-2.5 text-sm font-medium text-slate-700 hover:bg-white hover:shadow-xl transition-all group"
        aria-label="Ask the docs"
      >
        <Sparkles className="w-4 h-4 text-emerald-600 group-hover:rotate-12 transition-transform" />
        <span className="hidden sm:inline">Ask the docs</span>
      </button>
      <button
        onClick={dismiss}
        data-testid="copilot-dismiss"
        aria-label="Hide Copilot"
        className="absolute -top-2 -right-2 w-6 h-6 rounded-full bg-white text-slate-700 border border-slate-300 shadow-md flex items-center justify-center hover:bg-slate-100 z-10"
      >
        <X className="w-3.5 h-3.5"/>
      </button>
      </div>
      )}

      {open && (
        <div
          className="fixed inset-0 z-50 flex items-start justify-center pt-[10vh] px-4 bg-slate-900/40 backdrop-blur-sm"
          onClick={() => setOpen(false)}
          data-testid="copilot-modal"
        >
          <div
            className="w-full max-w-2xl bg-white rounded-2xl shadow-2xl border border-slate-200 overflow-hidden"
            onClick={(e) => e.stopPropagation()}
          >
            {/* Header */}
            <div className="flex items-center gap-3 px-5 py-4 border-b border-slate-100">
              <Sparkles className="w-5 h-5 text-emerald-600" />
              <div className="flex-1">
                <div className="text-sm font-semibold text-slate-900">Copilot</div>
                <div className="text-xs text-slate-500">Ask anything about Central Rentals</div>
              </div>
              <div className="flex items-center gap-1 rounded-lg bg-slate-100 p-0.5">
                <button
                  onClick={() => setMode("ask")}
                  className={`text-xs px-2.5 py-1 rounded-md font-medium ${mode === "ask" ? "bg-white shadow text-slate-900" : "text-slate-500"}`}
                  data-testid="copilot-mode-ask"
                >Ask</button>
                <button
                  onClick={() => setMode("search")}
                  className={`text-xs px-2.5 py-1 rounded-md font-medium ${mode === "search" ? "bg-white shadow text-slate-900" : "text-slate-500"}`}
                  data-testid="copilot-mode-search"
                >Search</button>
              </div>
              <button onClick={() => setOpen(false)} className="p-1 text-slate-400 hover:text-slate-700" data-testid="copilot-close-btn">
                <X className="w-5 h-5" />
              </button>
            </div>

            {/* Input */}
            <div className="px-5 py-4 border-b border-slate-100">
              <div className="flex items-center gap-3 rounded-xl border border-slate-200 focus-within:border-emerald-500 focus-within:ring-2 focus-within:ring-emerald-100 px-3 py-2.5">
                {mode === "ask" ? <Sparkles className="w-4 h-4 text-slate-400" /> : <Search className="w-4 h-4 text-slate-400" />}
                <input
                  ref={inputRef}
                  data-testid="copilot-input"
                  value={q}
                  onChange={(e) => setQ(e.target.value)}
                  onKeyDown={(e) => e.key === "Enter" && submit()}
                  placeholder={mode === "ask" ? "How do I collect rent with Stripe?" : "Search help docs…"}
                  className="flex-1 bg-transparent text-sm outline-none placeholder:text-slate-400"
                />
                <button
                  onClick={submit}
                  disabled={loading || !q.trim()}
                  data-testid="copilot-submit-btn"
                  className="flex items-center gap-1 text-xs font-medium bg-emerald-600 text-white rounded-lg px-3 py-1.5 disabled:opacity-40 hover:bg-emerald-700"
                >
                  {loading ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <ArrowRight className="w-3.5 h-3.5" />}
                  {loading ? "…" : "Go"}
                </button>
              </div>
            </div>

            {/* Results */}
            <div className="max-h-[50vh] overflow-y-auto px-5 py-4">
              {error && (
                <div className="text-sm text-red-600 bg-red-50 border border-red-200 rounded-lg px-3 py-2" data-testid="copilot-error">{error}</div>
              )}
              {!error && !loading && !answer && hits.length === 0 && (
                <div className="text-sm text-slate-500 space-y-2">
                  <div className="font-medium text-slate-700">Try one of these:</div>
                  {[
                    "How do I collect rent with Stripe?",
                    "What's included in the Ontario N4 notice?",
                    "How does tenant screening work in Canada?",
                    "Explain the CRA T776 export.",
                  ].map((s) => (
                    <button
                      key={s}
                      onClick={() => { setQ(s); setTimeout(submit, 50); }}
                      className="block text-left text-emerald-700 hover:text-emerald-800 hover:underline"
                      data-testid={`copilot-suggest-${s.slice(0, 12)}`}
                    >
                      → {s}
                    </button>
                  ))}
                </div>
              )}

              {answer && (
                <div className="space-y-4" data-testid="copilot-answer">
                  <div className="text-sm text-slate-800 whitespace-pre-wrap leading-relaxed">
                    {answer.answer}
                  </div>
                  {answer.source === "fallback" && (
                    <div className="text-[11px] text-amber-700 bg-amber-50 border border-amber-200 rounded px-2 py-1 inline-block hidden">
                      {/* Backup notice suppressed per product decision — the fallback answer stands on its own. */}
                    </div>
                  )}
                  {(answer.citations || []).length > 0 && (
                    <div className="pt-3 border-t border-slate-100">
                      <div className="text-xs font-semibold text-slate-500 uppercase tracking-wide mb-2">Sources</div>
                      <div className="space-y-1.5">
                        {answer.citations.map((c) => (
                          <a
                            key={c.id}
                            href={c.url || "#"}
                            className="flex items-center justify-between text-xs text-slate-600 hover:text-emerald-700 group"
                            data-testid={`copilot-citation-${c.id}`}
                          >
                            <span className="flex items-center gap-1.5">
                              <span className="inline-flex items-center justify-center w-4 h-4 rounded bg-slate-100 text-slate-500 text-[10px] font-semibold">{c.n}</span>
                              {c.title}
                            </span>
                            {c.url && <ExternalLink className="w-3 h-3 opacity-0 group-hover:opacity-100" />}
                          </a>
                        ))}
                      </div>
                    </div>
                  )}
                </div>
              )}

              {hits.length > 0 && (
                <div className="space-y-3" data-testid="copilot-hits">
                  {hits.map((h) => (
                    <a
                      key={h.id}
                      href={h.url || "#"}
                      className="block rounded-lg border border-slate-200 hover:border-emerald-400 hover:shadow-sm px-3 py-2.5 transition"
                      data-testid={`copilot-hit-${h.id}`}
                    >
                      <div className="flex items-start justify-between gap-2">
                        <div className="text-sm font-semibold text-slate-900">{h.title}</div>
                        <div className="text-[10px] text-slate-400 font-mono shrink-0">{Math.round(h.score * 100)}%</div>
                      </div>
                      <div className="text-xs text-slate-600 mt-1 line-clamp-2">{h.snippet}</div>
                    </a>
                  ))}
                </div>
              )}
            </div>
          </div>
        </div>
      )}
    </>
  );
}
